/**
 * Permanent top status bar for the Balloon (Frogger) game.
 *
 * Renders an opaque grass-coloured strip across the full width of the screen
 * with four stat chips (PONT / SOR / ÉLET / IDŐ) on the left and a row of
 * small decorative props that lead the eye toward the help / leaderboard
 * buttons (which live in `SceneChrome`) on the right.
 *
 * The bar is purely cosmetic — the parent scene is responsible for sizing
 * the playable area and keeping the player below it. The bar reports its
 * height via the static `HEIGHT` export so callers can lay out around it
 * without hard-coding the value twice.
 *
 * `update({ score, row, lives, time })` is the only mutator — call once per
 * frame from the scene's HUD-update step. Strings are passed verbatim, so
 * the caller can format e.g. `row` as `"12/45"` for fixed mode.
 */

import { Container, Graphics, Text, TextStyle } from '../../../vendor/pixi.min.mjs';
import { t } from '../../config/i18n.js';
import { FROGGER_CONFIG } from './froggerConfig.js';
import { paintChip, darken } from '../../ui/shared.js';

const BAR_H = FROGGER_CONFIG.TOP_BAR_H;
const BAR_BG_COLOR = FROGGER_CONFIG.COLOR_SAFE_ZONE;

const CHIP_W = 178;
const CHIP_H = 78;
const CHIP_GAP = 14;
// Sharp-cornered chips — per UX direction the in-game top header
// uses 0-radius corners across all games so the strip reads as a
// single uniform billboard. Inner highlight + stroke layers in
// `paintChip` cascade off this value.
const CHIP_RADIUS = 0;
const CHIP_LEFT_MARGIN = 20;
const CHIP_TOP_MARGIN = (BAR_H - CHIP_H) / 2;

const ICON_SIZE = 40;
const ICON_BG_RADIUS = 28;
const ICON_BG_X = CHIP_H / 2 - 2;

const TEXT_LEFT = CHIP_H + 4;

const LABEL_STYLE = new TextStyle({
    fontFamily: 'Poppins, Arial, sans-serif',
    fontSize: 13,
    fontWeight: '800',
    fill: '#ffffff',
    letterSpacing: 1.2,
    dropShadow: { color: '#000000', alpha: 0.3, distance: 1, blur: 1 },
});

const VALUE_STYLE = new TextStyle({
    fontFamily: 'Poppins, Arial, sans-serif',
    fontSize: 26,
    fontWeight: '900',
    fill: '#ffffff',
    dropShadow: { color: '#000000', alpha: 0.4, distance: 1, blur: 2 },
});

const CHIP_DEFS = [
    { id: 'score', labelKey: 'common.stat_score', color: 0xfbbf24, iconSlot: 'frogger.hud.score' },
    { id: 'row',   labelKey: 'common.stat_row',   color: 0xec4899, iconSlot: 'frogger.hud.row' },
    { id: 'lives', labelKey: 'common.stat_lives', color: 0xa855f7, iconSlot: 'frogger.hud.life' },
    { id: 'time',  labelKey: 'common.stat_time',  color: 0xfb923c, iconSlot: 'frogger.hud.timer' },
];

/** Small props sprinkled across the bar's right side. Each is best-effort —
 * if the slot has no skin, it is silently skipped. */
const DECOR_DEFS = [
    { slot: 'frogger.decor.tree',     w: 70, h: 90,  bottomPad: 0 },
    { slot: 'frogger.decor.bench',    w: 90, h: 56,  bottomPad: 4 },
    { slot: 'frogger.decor.lamp',     w: 32, h: 92,  bottomPad: 0 },
    { slot: 'frogger.decor.flowers',  w: 60, h: 48,  bottomPad: 6 },
    { slot: 'frogger.decor.pinetree', w: 68, h: 100, bottomPad: -8 },
];

export class FroggerTopBar {
    /** @type {Container} */
    container = new Container();

    /** Static height — exported for the scene's layout math. */
    static HEIGHT = BAR_H;

    _game = null;
    _chips = {};
    /**
     * X past which decorations / chrome buttons should not encroach.
     * Reserves room for the right-edge chrome cluster: the BACK pill plus
     * the HU/EN language pill that now sits just left of it (back 300 +
     * gap/pill ~112). Without this, the rightmost decoration would render
     * under the language toggle.
     */
    _rightSafeZone = 472;

    constructor(game) {
        this._game = game;
        this._build();
    }

    get height() { return BAR_H; }

    /**
     * Update visible chip values. Caller should pre-format `row` and `time`.
     * @param {{ score: number|string, row: number|string, lives: number|string, time: number|string }} stats
     */
    update(stats) {
        if (this._chips.score) this._chips.score._valueText.text = String(stats.score ?? 0);
        if (this._chips.row)   this._chips.row._valueText.text   = String(stats.row ?? 0);
        if (this._chips.lives) this._chips.lives._valueText.text = String(stats.lives ?? 0);
        if (this._chips.time)  this._chips.time._valueText.text  = String(stats.time ?? '0:00');
    }

    destroy() {
        this.container.removeChildren();
        if (this.container.parent) this.container.parent.removeChild(this.container);
        this._game = null;
        this._chips = {};
    }

    _build() {
        const w = this._game.width;

        const bg = new Graphics();
        bg.rect(0, 0, w, BAR_H);
        bg.fill(BAR_BG_COLOR);
        bg.rect(0, BAR_H - 1, w, 5);
        bg.fill({ color: 0x000000, alpha: 0.18 });
        this.container.addChild(bg);

        let x = CHIP_LEFT_MARGIN;
        for (const def of CHIP_DEFS) {
            const chip = this._buildChip(def);
            chip.x = x;
            chip.y = CHIP_TOP_MARGIN;
            this.container.addChild(chip);
            this._chips[def.id] = chip;
            x += CHIP_W + CHIP_GAP;
        }

        const decorStartX = x + 30;
        const decorEndX = w - this._rightSafeZone;
        if (decorEndX > decorStartX) {
            this._buildDecorations(decorStartX, decorEndX);
        }
    }

    _buildChip(def) {
        const chip = new Container();

        // v2 bevel stack — same recipe as `SnakeTopBar` so the two games
        // share a single chip aesthetic (dark rim → main fill → faux
        // gradient highlight on the top half → faded white inner stroke).
        // `shadowAlpha: 0` drops the drop-shadow per UX direction — the
        // sharp-corner top-header reads as flat ribbons rather than
        // floating cards.
        const face = paintChip({
            w: CHIP_W,
            h: CHIP_H,
            r: CHIP_RADIUS,
            color: def.color,
            shadowAlpha: 0,
        });
        chip.addChild(face);

        // Icon medallion — white circle with a darker stroke tinted from
        // the chip's base colour, so the icon pops off the chip body.
        const rim = darken(def.color, 0.55);
        const iconBg = new Graphics();
        iconBg.circle(ICON_BG_X, CHIP_H / 2, ICON_BG_RADIUS);
        iconBg.fill({ color: 0xffffff, alpha: 0.92 });
        iconBg.stroke({ color: rim, width: 3 });
        chip.addChild(iconBg);

        const icon = this._game.theme?.createSprite(def.iconSlot, ICON_SIZE, ICON_SIZE, {
            anchorX: 0.5, anchorY: 0.5, fitMode: 'contain',
        });
        if (icon) {
            icon.x = ICON_BG_X;
            icon.y = CHIP_H / 2;
            chip.addChild(icon);
        }

        const label = new Text({ text: t(def.labelKey), style: LABEL_STYLE });
        label.x = TEXT_LEFT;
        label.y = 14;
        chip.addChild(label);

        const value = new Text({ text: '0', style: VALUE_STYLE });
        value.x = TEXT_LEFT;
        value.y = 34;
        chip.addChild(value);

        chip._valueText = value;
        return chip;
    }

    _buildDecorations(startX, endX) {
        // Lay decorations bottom-aligned to the bar, walking left → right.
        // Pick a varied sequence so the eye doesn't see two of the same prop
        // back-to-back.
        let x = startX;
        const order = [0, 3, 2, 1, 4, 3, 0, 2]; // intentional non-monotone pattern
        for (const idx of order) {
            const def = DECOR_DEFS[idx % DECOR_DEFS.length];
            if (!def) continue;
            if (x + def.w > endX) break;
            const sprite = this._game.theme?.createSprite(def.slot, def.w, def.h, {
                anchorX: 0, anchorY: 0, fitMode: 'contain',
            });
            if (sprite) {
                sprite.x = x;
                sprite.y = BAR_H - def.h - (def.bottomPad ?? 0);
                this.container.addChild(sprite);
            }
            x += def.w + 22;
        }
    }
}
